import React, {type FC, useMemo} from 'react';

import {Button} from '../../../../../Button';
import {TextField} from '../../../../../Form/TextField';

type Props = {
  query: string;
  setQuery: (v: string) => void;
  filterError?: string;
  placeholder: string;
  onClear: () => void;
  autoFocus: boolean;
};

/**
 * Small controlled filter used by the ObjectViewerSection for inputs.
 *
 * Args:
 *   query: Current filter query string
 *   setQuery: Setter for the query
 *   filterError: Optional error message to display when JSONPath fails
 *
 * Examples:
 *   <InputsFilter query={q} setQuery={setQ} filterError={err} />
 */
const InputsFilter: FC<Props> = ({
  query,
  setQuery,
  filterError,
  placeholder,
  onClear,
  autoFocus,
}) => {
  const extraActions = useMemo(() => {
    return (
      <Button
        variant="ghost"
        size="small"
        icon="close"
        onClick={() => {
          setQuery('');
          onClear();
        }}
        className="mr-6"
      />
    );
  }, [setQuery, onClear]);

  return (
    <div key="inputs-filter">
      <TextField
        value={query}
        onChange={setQuery}
        placeholder={placeholder}
        size="small"
        icon="filter-alt"
        errorState={Boolean(query.trim() && filterError)}
        extraActions={extraActions}
        autoFocus={autoFocus}
      />
      {query.trim() && filterError && (
        <div
          style={{
            fontSize: '12px',
            color: 'var(--wb-status-error-text)',
            marginTop: '4px',
            marginBottom: '8px',
            paddingLeft: '8px',
          }}>
          JSONPath error: {filterError}. Showing text matches.
        </div>
      )}
    </div>
  );
};

export default InputsFilter;
